function csrfToken() {
  const tag = document.querySelector("meta[name='csrf-token']");
  return tag ? tag.content : "";
}

function appendMessage(container, text, type, links, disclaimer, personalization) {
  const message = document.createElement("div");
  message.className = `petter-ai-chat__message petter-ai-chat__message--${type}`;
  message.textContent = text;

  if (Array.isArray(links) && links.length > 0) {
    const list = document.createElement("div");
    list.className = "petter-ai-chat__links";
    links.forEach((link) => {
      const anchor = document.createElement("a");
      anchor.href = link.url;
      anchor.textContent = link.label;
      if (/^https?:\/\//.test(link.url)) {
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
      }
      list.appendChild(anchor);
    });
    message.appendChild(list);
  }

  if (disclaimer) {
    const note = document.createElement("small");
    note.textContent = disclaimer;
    message.appendChild(note);
  }

  if (personalization && personalization.enabled) {
    const context = document.createElement("small");
    const labels = Array.isArray(personalization.data_labels) ? personalization.data_labels : [];
    if (labels.length > 0) {
      context.textContent = `参照: ${labels.join("・")}`;
    } else if (personalization.selection_required) {
      context.textContent = "参照: 相談する子を選んでください";
    } else {
      context.textContent = "参照: 登録済み情報なし";
    }
    context.className = "petter-ai-chat__context";
    message.appendChild(context);
  }

  container.appendChild(message);
  container.scrollTop = container.scrollHeight;
}

function setupPetterAiChat() {
  const root = document.querySelector("[data-petter-ai-chat]");
  if (!root || root.dataset.initialized === "true") return;
  root.dataset.initialized = "true";

  const panel = root.querySelector("[data-ai-chat-panel]");
  const messages = root.querySelector("[data-ai-chat-messages]");
  const form = root.querySelector("[data-ai-chat-form]");
  const input = root.querySelector("[data-ai-chat-input]");
  const endpoint = root.dataset.endpoint;
  const quotaEndpoint = root.dataset.quotaEndpoint;
  const quotaStatus = root.querySelector("[data-ai-chat-quota]");
  const submitButton = form.querySelector("button[type=submit]");
  const sampleButtons = root.querySelectorAll("[data-ai-chat-sample]");
  let currentQuota = null;
  let submitting = false;
  let quotaLoading = false;
  let resetTimer = null;
  const personalizationEndpoint = root.dataset.personalizationEndpoint;
  const personalizationToggle = root.querySelector("[data-ai-personalization-toggle]");
  const personalizationStatus = root.querySelector("[data-ai-personalization-status]");
  const petSelect = root.querySelector("[data-ai-pet-select]");
  const conversation = [];
  const isSmallScreen = () => window.matchMedia("(max-width: 575.98px)").matches;

  const updateViewportHeight = () => {
    const height = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    document.documentElement.style.setProperty("--petter-ai-vh", `${Math.max(360, Math.round(height))}px`);
  };

  const updateControls = () => {
    const disabled = submitting || quotaLoading || !currentQuota || !currentQuota.available;
    input.disabled = disabled;
    submitButton.disabled = disabled;
    sampleButtons.forEach((button) => { button.disabled = disabled; });
    form.setAttribute("aria-busy", submitting ? "true" : "false");
  };

  const applyQuota = (quota) => {
    currentQuota = quota;
    if (quotaStatus) quotaStatus.textContent = `1人1日3回・サイト全体1日50回。${quota.message}`;
    updateControls();
    window.clearTimeout(resetTimer);
    const delay = Date.parse(quota.resets_at) - Date.now();
    if (delay > 0 && delay < 86400000) {
      resetTimer = window.setTimeout(() => {
        if (document.contains(root)) refreshQuota();
      }, delay + 1000);
    }
  };

  const refreshQuota = () => {
    if (quotaLoading || submitting) return;
    quotaLoading = true;
    updateControls();
    fetch(quotaEndpoint, { headers: { "Accept": "application/json" }, cache: "no-store" })
      .then((response) => response.json())
      .then((data) => {
        if (!data.quota) throw new Error("quota unavailable");
        applyQuota(data.quota);
      })
      .catch(() => {
        applyQuota({ available: false, message: "利用回数を確認できませんでした。AI相談を閉じて開き直すと再確認できます。施設検索・Q&Aは通常どおり使えます。" });
      })
      .finally(() => {
        quotaLoading = false;
        updateControls();
      });
  };

  const rememberMessage = (role, content) => {
    conversation.push({ role, content });
    if (conversation.length > 8) conversation.shift();
  };

  document.querySelectorAll("[data-ai-chat-toggle]").forEach((button) => {
    if (button.dataset.aiToggleBound === "true") return;
    button.dataset.aiToggleBound = "true";
    button.addEventListener("click", () => {
      root.classList.toggle("is-open");
      updateViewportHeight();
      if (root.classList.contains("is-open")) {
        refreshQuota();
        if (!isSmallScreen() && !input.disabled) input.focus();
      }
    });
  });

  root.querySelector("[data-ai-chat-close]").addEventListener("click", () => {
    root.classList.remove("is-open");
  });

  root.querySelectorAll("[data-ai-chat-sample]").forEach((button) => {
    button.addEventListener("click", () => {
      if (button.disabled || submitting) return;
      input.value = button.dataset.aiChatSample;
      form.requestSubmit();
    });
  });

  if (personalizationToggle) {
    personalizationToggle.addEventListener("change", () => {
      const enabled = personalizationToggle.checked;
      personalizationToggle.disabled = true;
      if (personalizationStatus) personalizationStatus.textContent = "更新中";

      fetch(personalizationEndpoint, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
          "Accept": "application/json"
        },
        body: JSON.stringify({ enabled })
      })
        .then((response) => {
          if (!response.ok) throw new Error("personalization update failed");
          return response.json();
        })
        .then((data) => {
          root.dataset.personalizationEnabled = data.enabled ? "true" : "false";
          if (petSelect) petSelect.disabled = !data.enabled;
          if (personalizationStatus) personalizationStatus.textContent = data.enabled ? "利用中" : "利用しない";
        })
        .catch(() => {
          personalizationToggle.checked = !enabled;
          if (petSelect) petSelect.disabled = enabled;
          if (personalizationStatus) personalizationStatus.textContent = "更新できませんでした";
        })
        .finally(() => {
          personalizationToggle.disabled = false;
        });
    });
  }

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const value = input.value.trim();
    if (!value || submitting || quotaLoading || !currentQuota || !currentQuota.available) return;
    submitting = true;
    updateControls();

    appendMessage(messages, value, "user");
    rememberMessage("user", value);
    input.value = "";
    const loading = "調べています...";
    appendMessage(messages, loading, "bot");
    const loadingNode = messages.lastElementChild;

    fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
        "Accept": "application/json"
      },
      body: JSON.stringify({
        message: value,
        history: conversation.slice(0, -1),
        pet_id: petSelect && !petSelect.disabled ? petSelect.value : null
      })
    })
      .then((response) => response.json())
      .then((data) => {
        if (typeof data.reply !== "string") throw new Error("invalid chat response");
        if (data.quota) applyQuota(data.quota);
        loadingNode.remove();
        appendMessage(messages, data.reply, "bot", data.links, data.disclaimer, data.personalization);
        rememberMessage("bot", data.reply);
      })
      .catch(() => {
        loadingNode.remove();
        appendMessage(messages, "すみません、返答を確認できませんでした。サーバーで受け付け済みの場合は1回分に数えられます。残り回数を確認してから再度お試しください。", "bot");
        currentQuota = null;
      })
      .finally(() => {
        submitting = false;
        updateControls();
        if (!currentQuota) refreshQuota();
      });
  });

  panel.addEventListener("click", (event) => {
    event.stopPropagation();
  });

  updateControls();
  updateViewportHeight();
  window.addEventListener("resize", updateViewportHeight);
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", updateViewportHeight);
    window.visualViewport.addEventListener("scroll", updateViewportHeight);
  }
}

document.addEventListener("turbolinks:load", setupPetterAiChat);
document.addEventListener("DOMContentLoaded", setupPetterAiChat);
